import React, { useCallback, useState, useEffect } from "react";
import { Graphics } from "@pixi/react";

const CenterNode = ({ 
  x, 
  y, 
  shape, 
  onNodeMouseDown, 
  onNodeUpdate,
  containerRef 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);

  const handleMouseDown = useCallback((event) => {
    event.stopPropagation();
    
    // Get global coordinates for the node
    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else {
      // Convert local to global coordinates
      const localPoint = event.data?.getLocalPosition(containerRef.current) || event;
      globalPoint = {
        x: shape.x + localPoint.x,
        y: shape.y + localPoint.y
      };
    }

    setIsDragging(true);
    setDragStart(globalPoint);
    onNodeMouseDown?.(event, "center", -1, globalPoint);
  }, [shape, onNodeMouseDown, containerRef]);

  const handleMouseMove = useCallback((event) => {
    if (!isDragging || !dragStart) return;

    // Get global coordinates properly
    let globalPoint;
    if (event.data?.global) {
      globalPoint = event.data.global;
    } else {
      // For DOM events, convert to stage coordinates
      const stage = containerRef.current?.parent?.parent;
      if (!stage) return;

      const canvas = stage.view || stage._canvas;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
      const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;

      globalPoint = {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY,
      };
    }

    // Move entire shape
    const deltaX = globalPoint.x - dragStart.x;
    const deltaY = globalPoint.y - dragStart.y;

    onNodeUpdate?.({
      x: shape.x + deltaX,
      y: shape.y + deltaY,
    });

    // Update drag start for continuous movement
    setDragStart(globalPoint);
  }, [isDragging, dragStart, shape, onNodeUpdate, containerRef]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  // Global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (event) => {
        const stage = containerRef.current?.parent?.parent;
        if (!stage) return;

        const canvas = stage.view || stage._canvas;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const scaleX = stage.app ? stage.app.screen.width / rect.width : 1;
        const scaleY = stage.app ? stage.app.screen.height / rect.height : 1;

        const globalPoint = {
          x: (event.clientX - rect.left) * scaleX,
          y: (event.clientY - rect.top) * scaleY,
        };

        const syntheticEvent = {
          clientX: event.clientX,
          clientY: event.clientY,
          data: {
            global: globalPoint,
          },
        };

        handleMouseMove(syntheticEvent);
      };

      document.addEventListener("mousemove", handleGlobalMouseMove);
      document.addEventListener("mouseup", handleMouseUp);

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, containerRef]);

  const drawNode = useCallback((g) => {
    g.clear();
    
    // Draw center node (circle with inner circle)
    g.lineStyle(1, 0xffffff); // white border
    g.beginFill(isHovered ? 0x32d96e : 0x27ae60); // lighter green on hover
    g.drawCircle(0, 0, 6);
    g.endFill();

    // Add small inner circle
    g.beginFill(0xffffff);
    g.drawCircle(0, 0, 2);
    g.endFill();
  }, [isHovered]);

  return (
    <Graphics
      x={x}
      y={y}
      draw={drawNode}
      interactive={true}
      cursor="move"
      pointerdown={handleMouseDown}
      pointermove={handleMouseMove}
      pointerup={handleMouseUp}
      pointerover={() => setIsHovered(true)}
      pointerout={() => setIsHovered(false)}
    />
  );
};

export default CenterNode; 